import { ReactElement, ReactNode, useEffect } from 'react'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import { refusePaneChange } from '../blockActionStore'
import type { SelectedPlanElement } from '../controlStore'
import { DEFAULT_MODE } from './modeSpecs'
import {
  SelectOptions,
  SelectionStateSnapshot,
  changesSelectionPane,
  isSubSelectableInMode,
  reconcileModeSwitch,
  reconcilePageEnter,
  reconcileSelect,
} from './reconcile'

interface Props {
  children: ReactNode
}

interface SelectPlanElementOptions {
  /**
   * Also switch the mode (reconciled like `switchMode`, so a primary selection the target mode
   * cannot hold is dropped) — how tables/image cards open their drawer form.
   */
  mode?: EditorMode
}

interface SelectionStoreInitialState extends SelectionStateSnapshot {
  selection: SceneElementSelection | null
  /** Secondary selection scoped to `selection`; cleared whenever the primary selection changes. */
  subSelection: SceneElementSubSelection | null
  /**
   * The selected plan element (comment / table / image card / element label). Independent of the
   * primary selection and the mode: a comment can be selected and edited in the scene while a
   * model element stays selected and keeps its form in the drawer. `select`, `switchMode`,
   * `enterPage` and `clearSelection` never touch it — only `selectPlanElement`, `escape` and `clear`.
   */
  planSelection: PlanElementSelection | null
  /** Always exactly one mode; `'neutral'` is the explicit no-tool mode. */
  mode: EditorMode
  /** Active tab of the form/pane belonging to the current selection (e.g. 'geometry'). */
  activeTab: string | null
  /** Element an area load is currently being drawn on (StructuralPlanning `area-load` mode). */
  drawAreaLoadElement: string | null
}

interface SelectionStoreType extends SelectionStoreInitialState {
  /**
   * Switch the mode; the selection/sub-selection survive iff the target mode can still hold them
   * (see `MODE_SPECS`). This is the only sanctioned way to change the mode.
   */
  switchMode: (mode: EditorMode) => void
  /**
   * Set the primary selection (plus optional sub-selection/tab) atomically. Without an explicit
   * `mode` option the mode follows the selection per the `adopt` policy — see `SelectOptions`.
   */
  select: (selection: SceneElementSelection | null, options?: SelectOptions) => void
  /** Set the sub-selection of the current primary selection. */
  selectSub: (
    subSelection: SceneElementSubSelection | null,
    options?: { activeTab?: string },
  ) => void
  /**
   * Set (or clear with `null`) the plan-element selection. Leaves the primary selection, the
   * sub-selection and — unless `mode` is passed — the mode alone.
   */
  selectPlanElement: (
    planSelection: PlanElementSelection | null,
    options?: SelectPlanElementOptions,
  ) => void
  /** Reconcile the mode with the page being entered (foreign page-bound mode → neutral). */
  enterPage: (page: EditorPage) => void
  /** One ESC step of the ladder: plan element → sub-selection → selection → mode back to neutral. */
  escape: () => void
  setActiveTab: (activeTab: string | null) => void
  setDrawAreaLoadElement: (drawAreaLoadElement: string | null) => void
  /** Clear selection and sub-selection but keep the mode (and the plan-element selection). */
  clearSelection: () => void
  clear: () => void
}

const initialState: SelectionStoreInitialState = {
  selection: null,
  subSelection: null,
  planSelection: null,
  mode: DEFAULT_MODE,
  activeTab: null,
  drawAreaLoadElement: null,
}

/** The reconciled part of the state; `planSelection` is orthogonal to it and never reconciled. */
const snapshotOf = (state: SelectionStoreInitialState): SelectionStateSnapshot => ({
  mode: state.mode,
  selection: state.selection,
  subSelection: state.subSelection,
  activeTab: state.activeTab,
  drawAreaLoadElement: state.drawAreaLoadElement,
})

const createStore = () =>
  create(
    combine({ ...initialState }, (set, get) => {
      /**
       * Apply a user-driven transition unless it leaves the drawer pane while a block action
       * refuses that (a form with unsaved changes, see `useBlockAction`'s `paneBlock`).
       */
      const transition = (
        reconcile: (current: SelectionStateSnapshot) => Partial<SelectionStateSnapshot>,
      ) => {
        const current = snapshotOf(get())
        const next = { ...current, ...reconcile(current) }

        if (changesSelectionPane(current, next) && refusePaneChange()) return

        set(next)
      }

      return {
        switchMode: (mode: EditorMode) => transition(state => reconcileModeSwitch(state, mode)),

        select: (selection: SceneElementSelection | null, options?: SelectOptions) =>
          transition(state => reconcileSelect(state, selection, options)),

        selectSub: (
          subSelection: SceneElementSubSelection | null,
          options?: { activeTab?: string },
        ) =>
          transition(state => {
            // Like `select`, a sub-selection write must respect the mode's capability matrix
            // (`MODE_SPECS`): a kind the current mode cannot hold is ignored rather than stored
            // as an impossible mode/sub-selection pairing. Clearing with `null` always goes through.
            if (subSelection !== null && !isSubSelectableInMode(state.mode, subSelection))
              return state

            return {
              subSelection,
              activeTab: options?.activeTab !== undefined ? options.activeTab : state.activeTab,
            }
          }),

        enterPage: (page: EditorPage) => set(state => reconcilePageEnter(snapshotOf(state), page)),

        escape: () => {
          if (get().planSelection) {
            // The plan element is the topmost layer (its HUD floats over the scene), so it closes
            // first — the element selection and its drawer form stay.
            set({ planSelection: null })
            return
          }

          transition(state => {
            if (state.subSelection) return { subSelection: null }

            if (state.selection) {
              // Multi-edit exists only through its multi-selection: clearing that must leave the
              // mode with it, or a second ESC would be needed to escape an empty multi-edit pane.
              const mode = state.mode === 'multi-edit' ? DEFAULT_MODE : state.mode
              return { selection: null, subSelection: null, activeTab: null, mode }
            }

            if (state.mode !== DEFAULT_MODE) return reconcileModeSwitch(state, DEFAULT_MODE)

            return state
          })
        },

        // Switching the tab dismisses the tab-scoped sub-selection (connector/transmitter/load/
        // stiffening); setting both together goes through `selectSub`/`select` with `activeTab`.
        setActiveTab: (activeTab: string | null) =>
          transition(() => ({ activeTab, subSelection: null })),

        selectPlanElement: (
          planSelection: PlanElementSelection | null,
          options?: SelectPlanElementOptions,
        ) => {
          const state = get()
          if (options?.mode === undefined) {
            // Clearing an already empty slot is a no-op (miss handlers of several plan element
            // controllers may all try to clear on the same click).
            if (planSelection === null && state.planSelection === null) return

            set({ planSelection })
            return
          }

          // A mode switch leaves the drawer pane, so it must respect a refusing block action.
          const current = snapshotOf(state)
          const next = { ...current, ...reconcileModeSwitch(current, options.mode) }
          if (changesSelectionPane(current, next) && refusePaneChange()) return

          set({ ...next, planSelection })
        },

        setDrawAreaLoadElement: (drawAreaLoadElement: string | null) =>
          set({ drawAreaLoadElement }),

        clearSelection: () => transition(() => ({ selection: null, subSelection: null })),

        clear: () => set({ ...initialState }),
      }
    }),
  )

/**
 * Global singleton (like the edit element store) rather than a context store, so imperative code
 * (module-level actions, `getState()` in callbacks) can access the selection without a provider.
 * The provider clears it when the editor unmounts, matching the context-store lifecycle.
 */
const useSelectionStore = createStore()

const SelectionStoreProvider = ({ children }: Props): ReactElement => {
  const clear = useSelectionStore(state => state.clear)

  useEffect(() => () => clear(), [clear])

  return <>{children}</>
}

/** Store api of the singleton (getState/subscribe) — compat with the zustand-context pattern. */
const useSelectionStoreApi = () => useSelectionStore

/** Imperative helper: clear the plan-element selection (comment/table/card/label), if any. */
const clearPlanElementSelection = () => {
  useSelectionStore.getState().selectPlanElement(null)
}

/** Imperative helper: the current plan-element selection (comment/table/card/label), or null. */
const getSelectedPlanElement = (): SelectedPlanElement | null => {
  const { planSelection } = useSelectionStore.getState()
  return planSelection ? { type: planSelection.planElementType, id: planSelection.guid } : null
}

export {
  SelectionStoreProvider,
  useSelectionStore,
  useSelectionStoreApi,
  clearPlanElementSelection,
  getSelectedPlanElement,
}
export { MODE_SPECS, DEFAULT_MODE, isGenericSceneClickMode } from './modeSpecs'
export { isSelectableInMode, isSubSelectableInMode, owningModeOf } from './reconcile'
export type { SelectionStoreType, SelectPlanElementOptions }
